import type { Feature, Point } from 'geojson';
import { useFiltersStore } from '@features/regional-overview/components/filters/filters.store';
import type { RegionalOverviewDigitalTrapsProperties } from 'querys/regional-overview/regional-overview.query.models';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDigitalTrapsStore } from '../../../digital-traps.zustand';
import type { ColumnPrediction, FlightPredictionRow } from '../../../domain/flight-prediction.models';
import { generateFlightPredictionColumnsAndKeys, processSingleTrapFlightPredictions } from '../../../utils';

export const useFlightPredictions = () => {
  const [t] = useTranslation();
  const selectedTrap = useDigitalTrapsStore(state => state.selectedTrap);
  const features = useFiltersStore(state => state.features) as Feature<Point, RegionalOverviewDigitalTrapsProperties>[];

  const { dates, columnPredictions } = useMemo(() => {
    const { dateKeys, dateTableColumns } = generateFlightPredictionColumnsAndKeys<FlightPredictionRow>({ t });
    const dates = dateTableColumns.map(column => (typeof column.title === 'string' ? column.title : ''));

    if (!selectedTrap) {
      return { dates, columnPredictions: [] as (ColumnPrediction | undefined)[] };
    }

    const { columnPredictions } = processSingleTrapFlightPredictions(selectedTrap, dateKeys);
    return { dates, columnPredictions: columnPredictions.map(prediction => prediction ?? undefined) };
  }, [selectedTrap, t]);

  const hasFlightPredictions = useMemo(() => {
    return Boolean(selectedTrap?.properties.flight_predictions?.predictions?.length);
  }, [selectedTrap]);

  const anyTrapHasFlightPredictions = useMemo(() => {
    return features.some(feature => Boolean(feature.properties.flight_predictions?.predictions?.length));
  }, [features]);

  return {
    dates,
    columnPredictions,
    hasFlightPredictions,
    anyTrapHasFlightPredictions
  };
};
